/**
 * 请求级缓存层 (自研, 无新依赖).
 *
 * 背景: 打开重页面 (如薪酬管理) 时, 大量组件/store 各自 fetch, 同一接口被打 2~3 遍,
 *   叠加 HTTP/1.1 6 连接上限排队 → 首屏被拖慢. 本层在 api/request.ts 的 GET 收口处插入,
 *   对调用方完全透明, 全站自动生效.
 *
 * 能力 1 — 并发去重 (in-flight dedup): 默认对所有 GET 开启.
 *   同一 (url + params) 若已有请求在飞, 复用同一 Promise; 每个 await 方拿到「独立克隆」,
 *   避免共享可变引用 (一方 in-place 改数组不影响另一方). 数学上永远正确 —— 只合并「同时在飞」
 *   的相同请求, 零陈旧风险.
 *
 * 能力 2 — TTL 缓存: 调用方显式 { cache: 毫秒 } 才开启, 仅用于静态配置/引用数据
 *   (字典/合同类型/薪资项目 meta 等). 交易数据 (工资单/调薪列表) 不要开.
 *   写操作 (POST/PUT/PATCH/DELETE) 会按 url 前缀自动失效对应缓存, 防脏读.
 */

type Fetcher = (url: string, config?: any) => Promise<any>

interface CacheEntry {
  value: any
  expiry: number
}

/** 正在飞行的 GET: key → Promise (未克隆的原始结果) */
const inflight = new Map<string, Promise<any>>()
/** TTL 缓存: key → { 原始值, 过期时间戳 } */
const ttlCache = new Map<string, CacheEntry>()

/** 稳定序列化 params (key 排序), 保证参数顺序不同也命中同一 key */
function stableQs(params: Record<string, any>): string {
  return Object.keys(params)
    .sort()
    .map((k) => `${k}=${JSON.stringify(params[k])}`)
    .join('&')
}

function keyOf(url: string, config?: any): string {
  const params = config?.params
  return params && Object.keys(params).length ? `${url}?${stableQs(params)}` : url
}

/** 浅拷贝够用: API 数据都是纯 JSON, 用 JSON 克隆隔离引用 */
function clone<T>(v: T): T {
  if (v == null || typeof v !== 'object') return v
  try {
    return JSON.parse(JSON.stringify(v))
  } catch {
    return v
  }
}

/**
 * 包装 GET: 并发去重 (默认) + 可选 TTL 缓存.
 * @param raw   原始 (未包装的) axios get, 已 bind 实例
 * @param url   请求 url
 * @param config 透传给 axios 的 config, 额外识别 { cache: 毫秒 }
 */
export function cachedGet(raw: Fetcher, url: string, config?: any): Promise<any> {
  // 非 JSON 响应 (blob/arraybuffer/文档流) 不能走去重+克隆 —— JSON 克隆会把 Blob 变成空对象 {}。
  //   直接透传原始请求 (文件下载/预览等场景)。
  const rt = config?.responseType
  if (rt && rt !== 'json' && rt !== 'text') {
    return raw(url, config)
  }

  const key = keyOf(url, config)
  const ttl = typeof config?.cache === 'number' ? config.cache : 0

  // 1. TTL 命中 (仅显式 { cache } 开启) → 直接返克隆
  if (ttl > 0) {
    const hit = ttlCache.get(key)
    if (hit && hit.expiry > Date.now()) return Promise.resolve(clone(hit.value))
  }

  // 2. 并发去重: 已有在飞的相同请求 → 复用 (各自克隆)
  const flying = inflight.get(key)
  if (flying) return flying.then(clone)

  // 3. 真正发起
  const p = raw(url, config)
    .then((value) => {
      if (ttl > 0) ttlCache.set(key, { value, expiry: Date.now() + ttl })
      return value
    })
    .finally(() => {
      inflight.delete(key)
    })

  inflight.set(key, p)
  return p.then(clone)
}

/** 写操作后按 url 前缀失效 TTL 缓存 (并发去重无需失效, 天然只活在飞行期间). */
export function invalidateByPrefix(prefix: string): void {
  if (!ttlCache.size) return
  for (const k of ttlCache.keys()) {
    if (k.startsWith(prefix)) ttlCache.delete(k)
  }
}

/** 清空全部 TTL 缓存 (登出等场景). */
export function clearApiCache(): void {
  ttlCache.clear()
  inflight.clear()
}
